import React from 'react';
import {
  OpsAgentModel,
  OpsAgentPerformanceModel,
  OpsFilterModel,
  OpsTeamModel
} from '../../../../shared/models/lead-ops.model';
import { getOpsAgentPerformance } from '../../../../shared/services/data/lead-ops.data';
import { useOpsReport } from '../components/use-ops-report.hook';
import { OpsSection, TD, TH } from '../components/ops-section.component';

const rate = (value: number) => `${value}%`;

/** A rate is only readable next to the volume it was computed from. */
const RateCell: React.FC<{ value: number; of: number }> = ({ value, of }) => (
  <>
    <span
      className={
        of === 0 ? 'text-neutral-300' : value >= 50 ? 'text-primary-600' : 'text-neutral-500'
      }
    >
      {of === 0 ? '—' : rate(value)}
    </span>
    {of > 0 ? <span className="ml-1 text-xs text-neutral-300">of {of}</span> : null}
  </>
);

export const TeamAndAgentsSection: React.FC<{ filter: OpsFilterModel }> = ({ filter }) => {
  const { data, loading, error, reload } = useOpsReport<OpsAgentPerformanceModel>(
    getOpsAgentPerformance,
    filter
  );

  const teams = data?.teams ?? [];
  const agents = data?.agents ?? [];

  return (
    <>
      <OpsSection
        title="Team scoreboard"
        subtitle="Agents folded under the manager they report to. Anyone whose manager is outside the tree collects under “No manager” rather than being dropped."
        accent="bg-primary-500"
        loading={loading}
        error={error}
        empty={!loading && !error && teams.length === 0}
        onRetry={reload}
      >
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead>
              <tr className="bg-creams-200">
                {['Manager', 'Headcount', 'Leads', 'Resolved', 'Won', 'Pending', 'Win rate'].map(
                  (column: string) => (
                    <th key={column} className={TH}>
                      {column}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {teams.map((team: OpsTeamModel) => (
                <tr key={team.managerId ?? 'none'} className="hover:bg-creams-100">
                  <td className={`${TD} font-gilroy-medium text-neutral-500`}>
                    {team.managerName}
                  </td>
                  <td className={`${TD} text-neutral-400`}>{team.headcount}</td>
                  <td className={`${TD} text-neutral-400`}>{team.total}</td>
                  <td className={`${TD} text-neutral-400`}>{team.resolved}</td>
                  <td className={`${TD} text-neutral-400`}>{team.won}</td>
                  <td className={`${TD} text-neutral-400`}>{team.pending}</td>
                  <td className={TD}>
                    <RateCell value={team.winRate} of={team.resolved} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </OpsSection>

      <OpsSection
        title="Agent leaderboard"
        subtitle={
          data && data.agentTotal > data.agents.length
            ? `Showing the top ${data.agents.length} of ${data.agentTotal} agents by volume.`
            : 'Win rate is won over resolved — an agent holding open leads has not lost them.'
        }
        accent="bg-secondary-400"
        loading={loading}
        error={error}
        empty={!loading && !error && agents.length === 0}
        onRetry={reload}
      >
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-sm">
            <thead>
              <tr className="bg-creams-200">
                {['Agent', 'Leads', 'Open', 'Resolved', 'Won', 'Lost', 'Win rate', 'SLA met'].map(
                  (column: string) => (
                    <th key={column} className={TH}>
                      {column}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {agents.map((agent: OpsAgentModel) => (
                <tr key={agent.userId} className="hover:bg-creams-100">
                  <td className={`${TD} font-gilroy-medium text-neutral-500`}>
                    {agent.userIdUser?.name ?? agent.userId}
                  </td>
                  <td className={`${TD} text-neutral-400`}>
                    {/*
                      The team decides whether someone can hold a lead at all,
                      so it belongs next to them rather than in the org chart.
                      An agent on no team should not be holding leads — say so
                      rather than leaving the cell blank.
                    */}
                    {agent.teamNames?.length ? (
                      agent.teamNames.join(', ')
                    ) : (
                      <span className="text-secondary-600">No team</span>
                    )}
                  </td>
                  <td className={`${TD} text-neutral-400`}>{agent.total}</td>
                  <td className={`${TD} text-neutral-400`}>
                    {agent.new + agent.pending + agent.reopened + agent.notReachable}
                  </td>
                  <td className={`${TD} text-neutral-400`}>{agent.resolved}</td>
                  <td className={`${TD} text-primary-600`}>{agent.won}</td>
                  <td className={`${TD} text-secondary-600`}>
                    {agent.notInterested + agent.notQualified}
                  </td>
                  <td className={TD}>
                    <RateCell value={agent.winRate} of={agent.resolved} />
                  </td>
                  <td className={`${TD} text-neutral-400`}>
                    {agent.slaMet + agent.slaBreached === 0 ? (
                      '—'
                    ) : (
                      <>
                        {agent.slaMet}
                        <span className="ml-1 text-xs text-neutral-300">
                          of {agent.slaMet + agent.slaBreached}
                        </span>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </OpsSection>
    </>
  );
};
